import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import moment from "moment";

function downloadCSV(rows, filename) {
  const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const MONTHS = [
    { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
    { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
    { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
    { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

function getYears() {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3];
}

const KNOWN_BRANCHES = ["K1", "K2", "OJC", "AMC"];
const CURRENT_YEAR = new Date().getFullYear();

export default function Amc_detail_report() {
    const { http, user } = AuthUser();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(String(CURRENT_YEAR));

    // Load current year on mount, and whenever year changes
    useEffect(() => {
        fetchYearReport(selectedYear);
    }, [selectedYear]);

    const fetchYearReport = (year) => {
        setIsLoading(true);
        http.get(`/get_amc_detail_report?login_user=${user.id}&year=${year}`)
            .then((res) => {
                setIsLoading(false);
                setData(Array.isArray(res.data.data) ? res.data.data : []);
            })
            .catch(() => { setIsLoading(false); setData([]); });
    };

    const handleYearChange = (e) => setSelectedYear(e.target.value);

    const years = getYears();

    // Build ordered branches — always show K1, K2, OJC, AMC; append any extras from data
    const presentBranches = [...new Set(data.map(r => r.branch_label))];
    const orderedBranches = [
        ...KNOWN_BRANCHES,
        ...presentBranches.filter(b => !KNOWN_BRANCHES.includes(b)),
    ];

    // Pivot: month_num -> { branch_label -> count }
    const pivot = {};
    MONTHS.forEach(m => { pivot[m.value] = {}; });
    data.forEach(r => {
        if (r.month_num) pivot[r.month_num][r.branch_label] = r.jc_count;
    });

    // Branch grand totals and overall grand total
    const branchTotals = {};
    orderedBranches.forEach(b => {
        branchTotals[b] = MONTHS.reduce((s, m) => s + (pivot[m.value]?.[b] || 0), 0);
    });
    const grandTotal = Object.values(branchTotals).reduce((s, v) => s + v, 0);

    const handleCSV = () => {
        const header = ["Month", ...orderedBranches, "Total"];
        const rows = MONTHS.map(m => {
            const rowTotal = orderedBranches.reduce((s, b) => s + (pivot[m.value]?.[b] || 0), 0);
            return [m.label, ...orderedBranches.map(b => pivot[m.value]?.[b] || 0), rowTotal];
        });
        const totalRow = ["TOTAL", ...orderedBranches.map(b => branchTotals[b]), grandTotal];
        downloadCSV([header, ...rows, totalRow], `amc_detail_report_${selectedYear}_${moment().format("YYYYMMDD_HHmm")}.csv`);
    };

    return (
        <>
            <div className="container mt-1">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h1 className="mb-0">Total Overall Report</h1>
                    <button className="btn btn-sm btn-outline-success" disabled={data.length === 0} onClick={handleCSV}>⬇ CSV</button>
                </div>
                <p className="text-muted">Number of JC (Job Cards) created in K1, K2, OJC and AMC tickets received</p>

                {/* Filters — Year only */}
                <div className="row align-items-end mb-3">
                    <div className="col-md-2 form-group mt-2">
                        <label className="form-label">Year</label>
                        <select className="form-control" value={selectedYear} onChange={handleYearChange}>
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : data.length === 0 ? (
                        <p className="text-center text-muted mt-3">No data found for {selectedYear}.</p>
                    ) : (
                        <table className="table table-bordered text-center" style={{ maxWidth: "720px" }}>
                            <thead className="table-primary">
                                <tr>
                                    <th className="text-start">Month</th>
                                    {orderedBranches.map(b => <th key={b}>{b}</th>)}
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MONTHS.map(m => {
                                    const rowTotal = orderedBranches.reduce((s, b) => s + (pivot[m.value]?.[b] || 0), 0);
                                    return (
                                        <tr key={m.value}>
                                            <td className="text-start fw-medium">{m.label} {selectedYear}</td>
                                            {orderedBranches.map(b => (
                                                <td key={b} className="fw-bold">
                                                    {(pivot[m.value]?.[b] || 0).toLocaleString("en-IN")}
                                                </td>
                                            ))}
                                            <td className="fw-bold text-primary">{rowTotal.toLocaleString("en-IN")}</td>
                                        </tr>
                                    );
                                })}
                                <tr style={{ backgroundColor: "#dce7ff", fontWeight: "bold" }}>
                                    <td className="text-start">TOTAL</td>
                                    {orderedBranches.map(b => (
                                        <td key={b}>{branchTotals[b].toLocaleString("en-IN")}</td>
                                    ))}
                                    <td className="text-primary">{grandTotal.toLocaleString("en-IN")}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}
